/**
 * React Query hook for managing multiple decks data
 * This hook provides a clean interface for fetching, caching, and managing
 * all decks data using React Query.
 */

import useApiClients from '@hooks/useApiClients.tsx';
import { Deck } from '@services/api/deckManagement/graphql/schema/API';
import { getAllDecks } from '@services/api/deckManagement/graphql/schema/queries';
import { useQuery } from '@tanstack/react-query';
import { extractGraphqlOperationName } from '@utils/graphqlOperationName';
import { createQueryOrMutationKey, createStandardQueryOptions, StandardQueryOptions } from '@utils/reactQueryConfig.ts';

// Query keys for React Query caching
export const decksQueryKeys = {
  all: createQueryOrMutationKey('decks'),
  deck: (deckId: string) => createQueryOrMutationKey(...decksQueryKeys.all, deckId),
  list: () => createQueryOrMutationKey(...decksQueryKeys.all, 'list'),
};

/**
 * Hook to fetch all decks for a user
 * @param options - Additional React Query options
 */
export const useAllDecks = (options?: StandardQueryOptions) => {
  const { deckManagementClient } = useApiClients();

  return useQuery({
    queryKey: decksQueryKeys.list(),
    queryFn: async (): Promise<Deck[]> => {
      // Call the GraphQL API using the imported query
      const response = await deckManagementClient.graphql({
        query: getAllDecks,
      });

      if ('data' in response && response.data?.getAllDecks) {
        // Filter out null entries that might be returned from GraphQL
        return response.data.getAllDecks.filter((deck): deck is Deck => deck !== null);
      }

      throw new Error('Failed to fetch decks');
    },
    ...createStandardQueryOptions<Deck[]>({ ...options, rumOp: extractGraphqlOperationName(getAllDecks) }),
  });
};
